import { BasicModal, Button } from '@doda/io-react/common';
import { CheckboxItemS } from 'components/CheckboxList/CheckboxItemS';
import { useCountUpdateAnimation } from 'hooks/useCountUpdateAnimation';
import { useEffect, useReducer, useRef, useState } from 'react';
import { UpperLimitErrorText } from 'components/UpperLimitErrorText';
import { SelectArea } from './components/SelectArea';
import styles from './index.module.scss';
import { CategoryItem } from './components/CategoryItem';
import { IndustryCheckList } from './components/IndustryCheckList';
import {
  CheckState,
  DisplayIndustryL,
  industryCheckReducer,
} from './logics/industryCheckReducer';
import {
  includeIndustryChildIds,
  removeIndustryChildIds,
} from './logics/organizeIndustryIds';

/**
 * Props interface
 */
interface Props {
  // モーダルの表示状態を示すフラグ。デフォルトは非表示
  show?: boolean;
  // 「閉じる」ボタンがクリックされたときのコールバック
  onClickClose: () => void;
  // 「検索」ボタンがクリックされたときのコールバック
  onClickSearch: (value: CheckState) => void;
  // 選択された業種が変更されたときのコールバック
  onChangeSelectedIndustry: (value: CheckState) => void;
  // 表示する業種データのリスト
  displayIndustryData: DisplayIndustryL[];
  // マッチする求人数
  matchJobCount: number;
  // サブミットボタンのテキスト。デフォルトは「この条件で設定する」
  submitButtonText?: string;
  // 選択された業種IDリスト
  selectedIndustryIdList?: Omit<CheckState, 'industry'>;
  // バリデーションフラグ
  validationFlags?: {
    isIncomeRangeError?: boolean;
    isConditionUpperLimitError?: boolean;
  };
}

/**
 * 業種選択用のモーダルコンポーネント
 * @param {Props} props コンポーネントのプロパティ
 * @returns {JSX.Element} IndustryModalコンポーネント
 */
export const IndustryModal: React.FC<Props> = ({
  show,
  onClickClose,
  onClickSearch,
  onChangeSelectedIndustry,
  displayIndustryData,
  matchJobCount,
  submitButtonText = 'この条件で設定する',
  selectedIndustryIdList,
  validationFlags = {},
}) => {
  const { isIncomeRangeError = true, isConditionUpperLimitError = true } =
    validationFlags;

  const [selectedJobType, setJobType] = useState<string | undefined>();
  const isSetFirstJobType = useRef(false);
  const selectedIndustryL = displayIndustryData.find(
    (industryL) => industryL.id === selectedJobType
  );

  const initialIndustryIdList = includeIndustryChildIds({
    industry: displayIndustryData,
    L: selectedIndustryIdList?.L || [],
    S: selectedIndustryIdList?.S || [],
  });

  const [checkedState, dispatch] = useReducer(
    industryCheckReducer,
    initialIndustryIdList
  );

  useEffect(() => {
    onChangeSelectedIndustry(removeIndustryChildIds(checkedState));
    // TODO: lintエラー修正後、削除する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedState]);

  useEffect(() => {
    if (!show) return;
    const updatedData = includeIndustryChildIds({
      industry: displayIndustryData,
      L: selectedIndustryIdList?.L || [],
      S: selectedIndustryIdList?.S || [],
    });

    dispatch({
      type: 'update',
      payload: updatedData,
    });
    // モーダルが開かれた時のみ、初期状態を更新する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const countAnimationRef = useCountUpdateAnimation(matchJobCount);
  const hasNoCheck =
    [...(checkedState?.L || []), ...(checkedState?.S || [])].length === 0;

  return (
    <BasicModal width="960px" onClickClose={onClickClose} show={show}>
      <div className={styles.industryModal__header}>
        <h2 className={styles.industryModal__title}>業種を絞り込む</h2>
        <span className={styles.industryModal__numberOfJobs}>
          この条件の求人数
          <span className={styles.number} ref={countAnimationRef} />件
        </span>
      </div>
      <div className={styles.industryModal__body}>
        <SelectArea title="分類を選択する">
          <ul>
            {displayIndustryData.map((industryL) => {
              const checked = checkedState.L.includes(industryL.id);

              const childIdList = industryL.industrySList.map(
                (indS) => indS.id
              );
              const checkedChild = childIdList.some((id) =>
                checkedState.S.includes(id)
              );

              // 初期レンダリング時に選択されている業種のうち、一番上のものをデフォルトで選択する
              if (checked && !selectedJobType && !isSetFirstJobType.current) {
                setJobType(industryL.id);
                isSetFirstJobType.current = true;
              }

              return (
                <CategoryItem
                  title={industryL.name}
                  numberOfJobs={industryL.jobCount}
                  selected={industryL.id === selectedJobType}
                  checked={checkedChild || checked}
                  onClick={() => {
                    setJobType(industryL.id);
                  }}
                  key={industryL.id}
                />
              );
            })}
          </ul>
        </SelectArea>
        <SelectArea title="業種を選択する">
          {selectedIndustryL ? (
            <IndustryCheckList
              title={selectedIndustryL.name}
              value={selectedIndustryL.id}
              numberOfJobs={selectedIndustryL.jobCount}
              onChange={() => {
                dispatch({
                  type: 'changeL',
                  value: {
                    id: selectedIndustryL.id,
                  },
                });
              }}
              checked={checkedState.L.includes(selectedIndustryL.id)}
            >
              {selectedIndustryL.industrySList.map((industryS) => {
                return (
                  <CheckboxItemS
                    title={industryS.name}
                    value={industryS.id}
                    checked={checkedState.S.includes(industryS.id)}
                    numberOfJobs={industryS.jobCount}
                    onChange={() => {
                      dispatch({
                        type: 'changeS',
                        value: {
                          id: industryS.id,
                          idL: selectedIndustryL.id,
                        },
                      });
                    }}
                    key={industryS.id}
                  />
                );
              })}
            </IndustryCheckList>
          ) : (
            <div className={styles.industryModal__notSelectedCategoryPanel}>
              分類を選択してください
            </div>
          )}
        </SelectArea>
      </div>
      <div className={styles.industryModal__footer}>
        {!isConditionUpperLimitError && <UpperLimitErrorText />}
        <Button
          size="M"
          onClick={() => onClickSearch(removeIndustryChildIds(checkedState))}
          variant="green"
          disabled={!isIncomeRangeError || !isConditionUpperLimitError}
        >
          {submitButtonText}
        </Button>
        <a
          href="#"
          onClick={(event) => {
            event.preventDefault();
            dispatch({ type: 'reset' });
          }}
          className={`${styles.industryModal__clearButton} ${
            hasNoCheck
              ? `${styles['industryModal__clearButton--disabled']}`
              : ''
          }`}
        >
          設定をクリア
        </a>
      </div>
    </BasicModal>
  );
};
